import { useEffect } from 'react';
import { MapboxMap } from '@/components/map/MapboxMap';
import { LocationDetailPanel } from '@/components/map/LocationDetailPanel';
import { LocationTypesStrip } from '@/components/map/LocationTypesStrip';
import { MapLegend } from '@/components/map/MapLegend';
import { DeviceMapState, DeviceMapActions } from './types';
import { useIsMobile } from '@/hooks/use-mobile';
import { LocationOverride } from '@/hooks/useLocationOverrides';
import '@/styles/map.css';

interface DeviceMapContainerProps {
  state: DeviceMapState;
  actions: DeviceMapActions;
  attentionScreenCounts: Map<number, number>;
  onAttentionScreenCountChange: (locationId: number, screenCount: number) => void;
  overrides?: Map<number, LocationOverride>;
}

export const DeviceMapContainer = ({ 
  state, 
  actions,
  attentionScreenCounts,
  onAttentionScreenCountChange,
  overrides
}: DeviceMapContainerProps) => {
  const { 
    filteredLocations, 
    selectedLocations, 
    activeRegion,
    activeMediaKind,
    activeMediaOwner,
    locations,
    focusedLocation
  } = state;
  
  const { handleLocationSelect, setFocusedLocation, handleToggleAllInRegion, handleToggleTypeInRegion } = actions;
  const isMobile = useIsMobile();

  // Close the focused location panel when the map context changes
  useEffect(() => {
    setFocusedLocation(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRegion, activeMediaKind, activeMediaOwner]);

  const isFocusedLocationSelected = focusedLocation ? selectedLocations.has(focusedLocation.id) : false;
  const focusedLocationScreenCount = focusedLocation ? (attentionScreenCounts.get(focusedLocation.id) ?? 1) : 0;

  return (
    <div className="relative bg-card dark:bg-card rounded-lg shadow-sm border border-border/40 overflow-hidden">
      <MapLegend activeMediaKind={activeMediaKind} />
      <div className={`w-full flex ${isMobile ? 'flex-col' : 'flex-row'} ${isMobile ? 'h-[50vh] min-h-[400px]' : 'h-[60vh]'}`}>
        {!isMobile && (
          <LocationDetailPanel
            location={focusedLocation}
            isSelected={isFocusedLocationSelected}
            selectedScreenCount={focusedLocationScreenCount}
            onClose={() => setFocusedLocation(null)}
            onSelect={handleLocationSelect}
            onScreenCountChange={onAttentionScreenCountChange}
            isMobile={false}
          />
        )}

        <div className="flex-1 h-full relative">
          <LocationTypesStrip
            activeRegion={activeRegion}
            activeMediaOwner={activeMediaOwner}
            activeMediaKind={activeMediaKind}
            locations={locations}
            selectedLocations={selectedLocations}
            onToggleType={handleToggleTypeInRegion}
            onToggleAll={handleToggleAllInRegion}
          />
          <MapboxMap
            activeRegion={activeRegion}
            filteredLocations={filteredLocations}
            selectedLocations={selectedLocations}
            onLocationSelect={handleLocationSelect}
            attentionScreenCounts={attentionScreenCounts}
            onAttentionScreenCountChange={onAttentionScreenCountChange}
            activeMediaKind={activeMediaKind}
            onLocationFocus={setFocusedLocation}
            focusedLocation={focusedLocation}
            isMobile={isMobile}
            overrides={overrides}
          />
        </div>
      </div>

      {isMobile && focusedLocation && (
        <LocationDetailPanel
          location={focusedLocation}
          isSelected={isFocusedLocationSelected}
          selectedScreenCount={focusedLocationScreenCount}
          onClose={() => setFocusedLocation(null)}
          onSelect={handleLocationSelect}
          onScreenCountChange={onAttentionScreenCountChange}
          isMobile={true}
        />
      )}
    </div>
  );
};
